// src/lib/errorUtils.js
/**
 * Standardized error handling utilities
 *
 * Provides consistent error handling patterns across the application
 */

import { logError } from "./logger";
import { captureException } from "./errorTracking";

/**
 * Error types for categorization
 */
export const ErrorType = {
  NETWORK: "network",
  AUTHENTICATION: "authentication",
  AUTHORIZATION: "authorization",
  VALIDATION: "validation",
  NOT_FOUND: "not_found",
  FIRESTORE_INDEX: "firestore_index",
  SERVER: "server",
  UNKNOWN: "unknown",
};

/**
 * Detect Firestore composite-index errors surfaced by client queries.
 */
export function isFirestoreIndexError(error) {
  if (!error) {
    return false;
  }
  const message = String(error.message || error.details || "").toLowerCase();
  if (!message) {
    return false;
  }
  if (message.includes("requires an index") || message.includes("create it here")) {
    return true;
  }
  if (message.includes("index") && message.includes("firestore")) {
    return true;
  }
  return error.code === FirebaseErrorCode.FAILED_PRECONDITION && message.includes("index");
}

/**
 * Firebase-specific error codes
 */
export const FirebaseErrorCode = {
  PERMISSION_DENIED: "permission-denied",
  UNAUTHENTICATED: "unauthenticated",
  NOT_FOUND: "not-found",
  ALREADY_EXISTS: "already-exists",
  FAILED_PRECONDITION: "failed-precondition",
  ABORTED: "aborted",
  OUT_OF_RANGE: "out-of-range",
  UNIMPLEMENTED: "unimplemented",
  INTERNAL: "internal",
  UNAVAILABLE: "unavailable",
  DATA_LOSS: "data-loss",
};

/**
 * Classify error type from error object
 */
export function classifyError(error) {
  if (!error) {
    return ErrorType.UNKNOWN;
  }

  // Network errors
  if (
    error.code === "network-error" ||
    error.message?.includes("network") ||
    error.message?.includes("fetch")
  ) {
    return ErrorType.NETWORK;
  }

  // Firebase Auth errors
  if (error.code?.startsWith("auth/") || error.code === FirebaseErrorCode.UNAUTHENTICATED) {
    return ErrorType.AUTHENTICATION;
  }

  // Firebase Permission errors
  if (error.code === FirebaseErrorCode.PERMISSION_DENIED) {
    return ErrorType.AUTHORIZATION;
  }

  if (isFirestoreIndexError(error)) {
    return ErrorType.FIRESTORE_INDEX;
  }

  // Not found errors
  if (error.code === FirebaseErrorCode.NOT_FOUND || error.status === 404) {
    return ErrorType.NOT_FOUND;
  }

  // Validation errors
  if (error.code === "validation-error" || error.name === "ValidationError") {
    return ErrorType.VALIDATION;
  }

  // Server errors
  if (
    error.status >= 500 ||
    error.code === FirebaseErrorCode.INTERNAL ||
    error.code === FirebaseErrorCode.UNAVAILABLE
  ) {
    return ErrorType.SERVER;
  }

  return ErrorType.UNKNOWN;
}

/**
 * Get user-friendly error message
 */
export function getUserFriendlyMessage(error, defaultMessage = "An unexpected error occurred") {
  if (
    error?.code === "functions/deadline-exceeded" ||
    error?.code === "deadline-exceeded"
  ) {
    return "The request timed out before the server finished. Flowhub sync and PDF generation can take up to 5 minutes — try again and keep this tab open.";
  }

  const errorType = classifyError(error);
  const errorCode = error?.code || error?.status;

  switch (errorType) {
    case ErrorType.NETWORK:
      return "Unable to connect to the server. Please check your internet connection and try again.";

    case ErrorType.AUTHENTICATION:
      if (errorCode === "auth/user-not-found") {
        return "User account not found. Please check your email address.";
      }
      if (errorCode === "auth/wrong-password") {
        return "Incorrect password. Please try again or reset your password.";
      }
      if (errorCode === "auth/email-already-in-use") {
        return "This email address is already registered. Please use a different email or sign in.";
      }
      if (errorCode === "auth/weak-password") {
        return "Password is too weak. Please use a stronger password (at least 6 characters).";
      }
      return "Your session has expired. Please sign in again.";

    case ErrorType.AUTHORIZATION:
      return "You don't have permission to perform this action. Please contact an administrator if you believe this is an error.";

    case ErrorType.FIRESTORE_INDEX:
      return "This page needs a Firestore composite index that is not deployed yet. An administrator must create the index before task data can load.";

    case ErrorType.NOT_FOUND:
      return "The requested resource was not found. It may have been deleted or moved.";

    case ErrorType.VALIDATION:
      return error.message || "Please check your input and try again.";

    case ErrorType.SERVER:
      return "The server encountered an error. Please try again in a few moments. If the problem persists, contact support.";

    default:
      // Try to extract a meaningful message from the error
      if (
        error?.message &&
        !error.message.includes("Error:") &&
        !error.message.includes("Failed")
      ) {
        return error.message;
      }
      return defaultMessage;
  }
}

/**
 * Get actionable guidance for error
 */
export function getErrorGuidance(error) {
  const errorType = classifyError(error);
  switch (errorType) {
    case ErrorType.NETWORK:
      return {
        action: "Check your internet connection",
        steps: [
          "Verify you're connected to the internet",
          "Try refreshing the page",
          "Check if other websites are working",
        ],
      };

    case ErrorType.AUTHENTICATION:
      return {
        action: "Sign in again",
        steps: [
          "Click the 'Sign In' button",
          "Enter your credentials",
          "If you forgot your password, use 'Forgot Password'",
        ],
      };

    case ErrorType.AUTHORIZATION:
      return {
        action: "Contact an administrator",
        steps: [
          "Your account may need additional permissions",
          "Contact your system administrator",
          "Provide your email address and the action you're trying to perform",
        ],
      };

    case ErrorType.FIRESTORE_INDEX:
      return {
        action: "Deploy the missing Firestore index",
        steps: [
          "Open the browser developer console (F12) and expand the failed network request for the full index URL, or check Cloud Functions / browser logs for a “create it here” link",
          "An admin can deploy indexes from firestore.indexes.json: firebase deploy --only firestore:indexes --project central-control-system-478401",
          "Wait for the index build to finish in Firebase Console → Firestore → Indexes (can take several minutes)",
          "Reload this page after the index status is Enabled",
        ],
      };

    case ErrorType.NOT_FOUND:
      return {
        action: "Navigate to a different page",
        steps: [
          "The resource you're looking for may have been removed",
          "Try navigating to the Dashboard",
          "Use the sidebar to access other sections",
        ],
      };

    case ErrorType.SERVER:
      return {
        action: "Wait and retry",
        steps: [
          "The server may be temporarily unavailable",
          "Wait a few moments and try again",
          "If the problem persists, contact support",
        ],
      };

    default:
      return {
        action: "Try again or contact support",
        steps: [
          "Refresh the page and try again",
          "Clear your browser cache if the problem persists",
          "Contact support if the issue continues",
        ],
      };
  }
}

/**
 * Handle error with logging and tracking
 */
export function handleError(error, context = {}) {
  const errorType = classifyError(error);
  const userMessage = getUserFriendlyMessage(error);
  const guidance = getErrorGuidance(error);

  // Log error
  logError(`[${errorType.toUpperCase()}] ${userMessage}`, error, {
    ...context,
    errorType,
    errorCode: error?.code || error?.status,
    guidance,
  });

  // Track error
  captureException(error, {
    tags: {
      errorType,
      source: context.source || "unknown",
    },
    extra: {
      ...context,
      userMessage,
      guidance,
    },
  });

  return {
    type: errorType,
    message: userMessage,
    guidance,
    originalError: error,
  };
}

/**
 * Create error object with consistent structure
 */
export function createError(message, type = ErrorType.UNKNOWN, code = null, originalError = null) {
  const error = new Error(message);
  error.type = type;
  error.code = code;
  error.originalError = originalError;
  error.timestamp = new Date().toISOString();
  return error;
}

/**
 * Check if error is retryable
 */
export function isRetryableError(error) {
  const errorType = classifyError(error);

  // Network errors are usually retryable
  if (errorType === ErrorType.NETWORK) {
    return true;
  }

  // Server errors (5xx) are usually retryable
  if (errorType === ErrorType.SERVER) {
    return true;
  }

  // Specific Firebase error codes that are retryable
  const retryableCodes = [
    FirebaseErrorCode.UNAVAILABLE,
    FirebaseErrorCode.ABORTED,
    FirebaseErrorCode.DATA_LOSS,
  ];

  if (error?.code && retryableCodes.includes(error.code)) {
    return true;
  }

  // HTTP status codes that are retryable
  if (error?.status >= 500 || error?.status === 429) {
    return true;
  }

  return false;
}

/**
 * Extract error message from various error formats
 */
export function extractErrorMessage(error) {
  if (!error) {
    return "An unknown error occurred";
  }

  // If it's already a string
  if (typeof error === "string") {
    return error;
  }

  // If it has a message property
  if (error.message) {
    return error.message;
  }

  // If it's a Firebase error with details
  if (error.details) {
    return error.details;
  }

  // If it's an error object with code
  if (error.code) {
    return `Error code: ${error.code}`;
  }

  // Fallback
  return "An unexpected error occurred";
}

/**
 * Throw when a Firebase callable returns success:false so result.data.error reaches formatters.
 */
export function throwUnlessCallableSuccess(result, fallbackMessage) {
  if (result?.data?.success) {
    return result.data;
  }
  throw new Error(result?.data?.error || fallbackMessage);
}
